import React, { useState, useEffect, useRef } from 'react';
import { Box, Modal, Grid, Zoom, Checkbox, FormControl, FormLabel, FormControlLabel, Radio, RadioGroup, Typography, TextField } from '@mui/material';
import { BackdropLoading } from '../../shared/spinner/backdropLoading';
import { CancelButton, ModalLabel, modalStyle, DlButton, ModalScroll, formCommon, itemLabel, itemRadio, itemBox, inputBox, inputField, explainText, FieldChoice } from './printCondition.styles';
import Bubble from './printDialogErrorBubble';
import {
    type SortingState,
} from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";
import { sessionDisconnectedCheck } from '../../shared/api/sessionDisconnectedCheck';
import { fetchAuthSession } from 'aws-amplify/auth';
import createPdf from '../../shared/api/createPdf';
import { SystemErrorMsg, SystemErrorTitle } from '../../constants';
import { CreatePDFApiRequest } from '../../shared/model/apiRequestModel';

interface OwnProps {
    isOpen: boolean,
    handleClose: (isOpen: boolean) => void
    handleValue: HandleValue,
}

interface HandleValue {
    kind: Kind,
    start: number,
    end: number,
    total: number,
    sorting: SortingState,
    condition: Condition[] | undefined,
}

type Kind = "一覧" | "詳細" | "宛名ラベル";

type FieldItem = {
    id: string,
    label: string
}

type Condition = {
    operator: string
    itemLabel: string
    count: number
    value: string
}

// ソートカラムのマップ定義
const sortColumn: { [key: string]: string } = {
    "SHOGO_KANJI": "SHOGO_KANA",
    "JUSHO": "JUSHO_CODE",
    "DAIHYOSHA_KANJI": "DAIHYOSHA_KANA",
    "KIGYO_CODE": "KIGYO_CODE",
    "YUBIN_NO": "YUBIN_NO",
    "SHIHONKIN": "SHIHONKIN",
    "JUGYOINSU": "JUGYOINSU",
    "URIAGE1": "URIAGE1",
    "RIEKI1": "RIEKI1"
}

//フィールド選択の全項目
const AllItem: Record<Kind, FieldItem[]> = {
    "一覧": [
        { id: "KIGYO_CODE", label: "企業コード" },
        { id: "SHOGO_KANJI", label: "商号" },
        { id: "DAIHYOSHA_KANJI", label: "代表者" },
        { id: "YUBIN_NO", label: "郵便番号" },
        { id: "DENWA_NO", label: "電話番号" },
        { id: "JUSHO", label: "所在地" },
    ],
    "詳細": [
        { id: "KIGYO_CODE", label: "企業コード" },
        { id: "JOJO", label: "上場" },
        { id: "CHOSA_NENGAPPI", label: "調査年月日" },
        { id: "SHOGO_KANA", label: "商号（カナ）" },
        { id: "DAIHYOSHA_KANA", label: "代表者カナ" },
        { id: "SHOGO_KANJI", label: "商号（漢字）" },
        { id: "DAIHYOSHA_KANJI", label: "代表者氏名" },
        { id: "YUBIN_NO", label: "郵便番号" },
        { id: "JUSHO", label: "所在地" },
        { id: "DENWA_NO", label: "電話番号" },
        { id: "SETSURITSU_NENGETSU", label: "設立年月" },
        { id: "SOGYO_NENGETSU", label: "創業年月" },
        { id: "SHIHONKIN", label: "資本金" },
        { id: "JUGYOINSU", label: "従業員数" },
        { id: "GYSH", label: "業種" },
        { id: "EIGYO_SHUMOKU", label: "営業種目" },
        { id: "EIGYOSHO_SHITEN", label: "営業所・支店" },
        { id: "YAKUIN", label: "役員" },
        { id: "SHIIRESAKI", label: "仕入先" },
        { id: "KABUNUSHI", label: "大株主" },
        { id: "HAMBAISAKI", label: "販売先" },
        { id: "GYOSEKI_ALL", label: "業績" },
        { id: "TORIHIKI_GINKO", label: "取引銀行" },
        { id: "GAIKYO", label: "概況" },
        { id: "DAIHYOSHA", label: "代表者　代表者氏名" },
        { id: "GENJUSHO", label: "代表者　代表者現住所" },
        { id: "SEINENGAPPI", label: "代表者　生年月日" },
        { id: "ETO", label: "代表者　干支" },
        { id: "SHUSSHINCHI", label: "代表者　出身地" },
        { id: "SHUSSHINKO", label: "代表者　出身校" },
    ],
    "宛名ラベル": [
        { id: "YUBIN_NO", label: "郵便番号" },
        { id: "JUSHO", label: "所在地" },
        { id: "SHOGO_KANJI", label: "商号（漢字）" },
        { id: "DAIHYOSHA_KANJI", label: "代表者氏名" },
    ],
};
const screenId = "U060"

//全チェックの初期値をtrueにする
const initialFlags = Object.fromEntries(
    Object.values(AllItem).flat().map((item) => [item.id, true]));

const PrintDialog = (props: OwnProps) => {
    const navigate = useNavigate();

    const { isOpen, handleClose } = props
    const [open, setOpen] = React.useState(isOpen);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [kind, setKind] = useState<Kind>(props.handleValue.kind);
    const [pdfType, setPdfType] = useState<"0" | "1" | "2">("0");
    const [start, setStart] = useState<number | null>();
    const [end, setEnd] = useState<number | null>();
    const [labelStart, setLabelStart] = useState<number | null>(1);
    const [stamp, setStamp] = useState("ラベル1");
    const [labelType, setLabelType] = useState<"1" | "2">("1");
    const [name, setName] = useState("");
    const [condition, setCondition] = useState(false);

    const [field, setField] = useState<FieldItem[]>([]);
    const [fieldCheck, setFieldCheck] = useState<Record<string, boolean>>(initialFlags);

    const [activeLabelStart, setActiveLabelStart] = useState(true);
    const [activeStamp, setActiveStamp] = useState(true);
    const [activeName, setActiveName] = useState(true);
    const [activeCondition, setActiveCondition] = useState(false);

    const containerRef = useRef<HTMLDivElement | null>(null);
    const inputRefS = useRef<HTMLInputElement | null>(null);
    const inputRefE = useRef<HTMLInputElement | null>(null);
    const inputRefL = useRef<HTMLInputElement | null>(null);
    const [pos, setPos] = useState({ top: 0, left: 0, right: 0 });

    const [errorStart, setErrorStart] = useState(false);
    const [errorEnd, setErrorEnd] = useState(false);
    const [errorLabel, setErrorLabel] = useState(false);
    const [startErrorMessage, setStartErrorMessage] = useState("");
    const [endErrorMessage, setEndErrorMessage] = useState("");
    const [labelErrorMessage, setLabelErrorMessage] = useState("");

    const [sortInfoDesc, setSortInfoDesc] = useState<"asc" | "desc">("desc");

    const resultNum = props.handleValue.total;
    //const resultNum = 1001; //100件確認用の固定値

    React.useEffect(() => {
        setOpen(isOpen);

        setKind(props.handleValue.kind);
        setStart(props.handleValue.start);
        setEnd(props.handleValue.end);
        setLabelStart(1);
        setStamp("ラベル1");
        setName("");
        setCondition(false);
        setFieldCheck(initialFlags);
        setStartErrorMessage("");
        setEndErrorMessage("");
        setLabelErrorMessage("");
        setErrorStart(false);
        setErrorEnd(false);
        setErrorLabel(false);

        props.handleValue.sorting[0].desc == true ? setSortInfoDesc("desc") : setSortInfoDesc("asc");

        if (kind == "一覧") setPdfType("0");
        else if (kind == "詳細") setPdfType("1");
        else setPdfType("2");
    }, [isOpen])

    //入力可能項目を変更する
    useEffect(() => {
        setErrorStart(false);
        setErrorEnd(false);
        setErrorLabel(false);
        const StartToEnd = (end ?? 0) - (start ?? 0);

        setFieldCheck(initialFlags);

        if (kind == "一覧") {
            setPdfType("0")
            setActiveLabelStart(true);
            setActiveStamp(true);
            setActiveName(true);
            setActiveCondition(false);

            if (StartToEnd >= 100) {
                const fixEnd = (start ?? 0) + 99;
                setEnd(fixEnd);
            }

            if ((labelStart ?? 0) == 0 || (labelStart ?? 0) > 12) {
                setLabelStart(1);
            }
        } else if (kind == "詳細") {
            setPdfType("1");
            setActiveLabelStart(true);
            setActiveStamp(true);
            setActiveName(true);
            setActiveCondition(true);

            if ((start ?? 0) != (end ?? 0))
                setEnd(start);

            if ((labelStart ?? 0) == 0 || (labelStart ?? 0) > 12) {
                setLabelStart(1);
            }
        } else {
            setPdfType("2");
            setActiveLabelStart(false);
            setActiveStamp(false);
            setActiveName(false);
            setActiveCondition(true);
        };

        setField(AllItem[kind]);
    }, [kind]);

    useEffect(() => {
        if (stamp == "ラベル1") setLabelType("1");
        else setLabelType("2");
    }, [stamp]);

    //不必要な桁数以上の数値以外入力できないように
    const startFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
        let v = e.target.value;

        v = v.replace(/[^0-9]/g, "");
        if (v.length > 4) v = v.slice(0, 4);

        setStart(v === "" ? null : Number(v));
    }

    const endFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
        let v = e.target.value;

        v = v.replace(/[^0-9]/g, "");
        if (v.length > 4) v = v.slice(0, 4);

        setEnd(v === "" ? null : Number(v));
    }

    const labelFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
        let v = e.target.value;

        v = v.replace(/[^0-9]/g, "");
        if (v.length > 2) v = v.slice(0, 2);

        setLabelStart(v === "" ? null : Number(v));
    }

    const nameFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
        let v = e.target.value;

        if (v.length > 21) v = v.slice(0, 21);

        setName(v);
    }

    //フィールド選択チェック変更
    const toggle = (id: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setFieldCheck((prev) => ({ ...prev, [id]: e.target.checked }))
    };

    //PDF種類の異なるフィールド選択は取らない
    const getItemsByKind = (kind: Kind) => {
        return Object.fromEntries(
            AllItem[kind].map(item => [item.id, fieldCheck[item.id]])
        );
    }

    //ボタン押下動作
    const DLCheck = async () => {
        setStartErrorMessage("");
        setEndErrorMessage("");
        setLabelErrorMessage("");
        setErrorStart(false);
        setErrorEnd(false);
        setErrorLabel(false);

        //開始番号エラーチェック
        if (inputRefS.current && containerRef.current) {
            const inputRectS = inputRefS.current?.getBoundingClientRect();
            const containerRectS = containerRef.current?.getBoundingClientRect();

            setPos({
                top: inputRectS.bottom - containerRectS.top + 40,
                left: inputRectS.left - containerRectS.left + 55,
                right: 0
            });
        }

        if (start == null) {
            setStartErrorMessage("開始番号が入力されていません");
            setErrorStart(true);
            return;
        } else if (start == 0 || start > resultNum) {
            setStartErrorMessage("出力可能範囲外の値です");
            setErrorStart(true);
            return;
        }

        //終了番号エラーチェック
        if (inputRefE.current && containerRef.current) {
            const inputRectE = inputRefE.current?.getBoundingClientRect();
            const containerRectE = containerRef.current?.getBoundingClientRect();

            setPos({
                top: inputRectE.bottom - containerRectE.top + 40,
                left: 0,
                right: containerRectE.right - inputRectE.right + 60,
            });
        }

        if (end == null) {
            setEndErrorMessage("終了番号が入力されていません");
            setErrorEnd(true);
            return;
        } else if (kind == "詳細" && end != start) {
            setEndErrorMessage("一度に出力可能な詳細は1社のみです");
            setErrorEnd(true);
            return;
        } else if (end - start < 0) {
            setEndErrorMessage("開始番号より小さい値が入力されています");
            setErrorEnd(true);
            return;
        } else if (end == 0 || end > resultNum) {
            setEndErrorMessage("出力可能範囲外の値です");
            setErrorEnd(true);
            return;
        } else if (kind == "一覧" && (end - start >= 100)) {
            setEndErrorMessage("出力範囲の上限は100社です");
            setErrorEnd(true);
            return;
        }

        //ラベル開始位置エラーチェック
        if (inputRefL.current && containerRef.current) {
            const inputRectL = inputRefL.current?.getBoundingClientRect();
            const containerRectL = containerRef.current?.getBoundingClientRect();

            setPos({
                top: inputRectL.bottom - containerRectL.top - 51,
                left: inputRectL.left - containerRectL.left + 55,
                right: 0
            });
        }

        if (labelStart == null) {
            setLabelErrorMessage("ラベル開始位置が入力されていません");
            setErrorLabel(true);
            return;
        } else if (labelStart == 0 || labelStart > 12) {
            setLabelErrorMessage("1～12の範囲で指定してください");
            setErrorLabel(true);
            return;
        }

        const outputColumn = Object.entries(getItemsByKind(kind))
            .filter(([, v]) => v)
            .map(([k]) => k);

        // セッションの有効性確認
        if (await sessionDisconnectedCheck(screenId, 'handleClickPDFDownload', navigate)) {
            return
        }
        setLoading(true)

        //API処理部分
        const session = await fetchAuthSession();
        const email = session.tokens?.idToken?.payload?.email?.toString() || '';
        const sessionId = session.tokens?.idToken?.payload?.sessionId?.toString() || '';
        const contract = JSON.parse(session?.tokens?.idToken?.payload.contractInfoData?.toString() || "[]");
        const personalInfo = contract.length !== 0 && contract[0].personalInfo;

        const request: CreatePDFApiRequest = {
            sessionId: sessionId,
            userMailAddress: email,
            pdfType: pdfType,
            detailNoStart: start,
            detailNoEnd: end,
            sorted: {
                item: sortColumn[props.handleValue.sorting[0].id],
                order: sortInfoDesc,
            },
            labelStart: labelStart,
            labelType: labelType,
            address: name,
            conditionDispFlag: condition,
            condition: props.handleValue.condition || [],
            outputColumn: outputColumn,
            personalInfo: personalInfo,
        }
        const result: any = await createPdf(request)
        // 帳票出力APIから正常終了以外が返却されたらエラー表示
        if (result != "I0000") {
            navigate("/Error", { state: { title: SystemErrorTitle, message: SystemErrorMsg } });
        }
        setLoading(false)
    }

    return (
        <>
            <BackdropLoading open={loading} />
            <Modal
                open={open}
                onClose={handleClose}
                closeAfterTransition
                sx={{
                    zIndex: 900
                }}
                slotProps={{
                    backdrop: {
                        sx: {
                            zIndex: 900
                        }
                    }
                }}
            >
                <Zoom
                    in={open}
                    style={{ zIndex: 1000, transformOrigin: '-50% -50% -50%' }}
                    easing={{
                        enter: "cubic-bezier(0, 1.45, 0.62, 1)",
                        exit: "linear"
                    }}
                >
                    <Box sx={modalStyle} tabIndex={-1}>
                        <ModalLabel>
                            PDF出力
                        </ModalLabel>
                        <ModalScroll>
                            <Box p={2}>
                                <FormControl sx={{ ...formCommon, marginTop: '-5px', height: '75px' }}>
                                    <FormLabel sx={itemLabel}>PDF種類</FormLabel>
                                    <RadioGroup sx={itemRadio} row value={kind} onChange={(e) => setKind(e.target.value as Kind)}>
                                        <FormControlLabel value="一覧" control={<Radio />} label={<Typography sx={{ fontSize: "18px" }}>一覧</Typography>}></FormControlLabel>
                                        <FormControlLabel value="詳細" control={<Radio />} label={<Typography sx={{ fontSize: "18px" }}>詳細</Typography>}></FormControlLabel>
                                        <FormControlLabel value="宛名ラベル" control={<Radio />} label={<Typography sx={{ fontSize: "18px" }}>宛名ラベル</Typography>}></FormControlLabel>
                                    </RadioGroup>
                                </FormControl>
                            </Box>
                            <Box p={2}>
                                <FormControl sx={{ ...formCommon, marginTop: '-25px', height: '85px' }}>
                                    <FormLabel sx={itemLabel}>出力範囲</FormLabel>
                                    <Box sx={itemBox} ref={containerRef}>
                                        <Box sx={{ ...inputBox, width: '22%' }}>
                                            開始番号
                                        </Box>
                                        <TextField variant='outlined' value={start} onChange={startFilter} size='small' sx={{ ...inputField, width: '18%', "& .MuiOutlinedInput-root": { "height": 35, "& fieldset": { borderColor: errorStart ? "#ff0000" : "rgba(0,0,0,0.23)" } } }} inputRef={inputRefS} />
                                        <Bubble open={startErrorMessage !== ""} sx={{ top: pos.top, left: pos.left }} pos={"left"}>{startErrorMessage}</Bubble>
                                        <Bubble open={endErrorMessage !== ""} sx={{ top: pos.top, right: pos.right }} pos={"right"}>{endErrorMessage}</Bubble>
                                        <span style={{ marginLeft: '25px', marginRight: '25px' }}>～</span>
                                        <Box sx={{ ...inputBox, width: '22%' }}>
                                            終了番号
                                        </Box>
                                        <TextField variant='outlined' value={end} onChange={endFilter} size='small' sx={{ ...inputField, width: '18%', "& .MuiOutlinedInput-root": { "height": 35, "& fieldset": { borderColor: errorEnd ? "#ff0000" : "rgba(0,0,0,0.23)" } } }} inputRef={inputRefE} />
                                    </Box>
                                </FormControl>
                            </Box>
                            <Box p={2}>
                                <FormControl sx={{ ...formCommon, marginTop: '-25px', height: '105px' }}>
                                    <FormLabel sx={itemLabel}>ラベル開始位置</FormLabel>
                                    <Box sx={itemBox}>
                                        <Box sx={{ ...inputBox, width: '22%' }}>
                                            開始位置
                                        </Box>
                                        <TextField disabled={activeLabelStart} variant='outlined' value={labelStart ?? ""} onChange={labelFilter} size='small' sx={{ ...inputField, width: '18%', '& .MuiOutlinedInput-root.Mui-disabled': { backgroundColor: '#d9d9d9' }, "& .MuiOutlinedInput-root": { "height": 35, "& fieldset": { borderColor: errorLabel ? "#ff0000" : "rgba(0,0,0,0.23)" } } }} inputRef={inputRefL} />
                                        <Bubble open={labelErrorMessage !== ""} sx={{ top: pos.top, left: pos.left }} pos={"left"}>{labelErrorMessage}</Bubble>
                                    </Box>
                                    <FormLabel sx={explainText}>※　1（左上）～12（右下）の範囲で指定できます</FormLabel>
                                </FormControl>
                            </Box>
                            <Box p={2}>
                                <FormControl sx={{ ...formCommon, marginTop: '-25px', height: '75px' }}>
                                    <FormLabel sx={itemLabel}>ラベル印紙</FormLabel>
                                    <RadioGroup sx={itemRadio} row value={stamp} onChange={(e) => setStamp(e.target.value)}>
                                        <FormControlLabel value="ラベル1" control={<Radio disabled={activeStamp} />} label={<Typography sx={{ fontSize: "18px" }}>ラベル1</Typography>}></FormControlLabel>
                                        <FormControlLabel value="ラベル2" control={<Radio disabled={activeStamp} />} label={<Typography sx={{ fontSize: "18px" }}>ラベル2</Typography>}></FormControlLabel>
                                    </RadioGroup>
                                </FormControl>
                            </Box>
                            <Box p={2}>
                                <FormControl sx={{ ...formCommon, marginTop: '-25px', height: '85px' }}>
                                    <FormLabel sx={itemLabel}>宛名</FormLabel>
                                    <Box sx={itemBox}>
                                        <Box sx={{ ...inputBox, width: '30%' }}>
                                            宛名（任意）
                                        </Box>
                                        <TextField disabled={activeName} variant='outlined' value={name} onChange={nameFilter} size='small' sx={{ ...inputField, width: '60%', '& .MuiOutlinedInput-root.Mui-disabled': { backgroundColor: '#d9d9d9' }, "& .MuiOutlinedInput-root": { "height": 35 } }} />
                                    </Box>
                                </FormControl>
                            </Box>
                            <Box p={2}>
                                <FormControl sx={{ ...formCommon, marginTop: '-25px', height: '80px' }}>
                                    <FormLabel sx={itemLabel}>検索条件</FormLabel>
                                    <Box sx={itemBox}>
                                        <FormControlLabel
                                            control={<Checkbox disabled={activeCondition} checked={condition} onChange={(e) => setCondition(e.target.checked)} sx={{ '& .MuiSvgIcon-root': { fontSize: '20px' } }} />}
                                            label="表示する" sx={{ '& .MuiFormControlLabel-label': { fontSize: '18px' } }} />
                                    </Box>
                                </FormControl>
                            </Box>
                            <Box p={2}>
                                <FormControl sx={{ ...formCommon, marginTop: '-25px', height: '230px' }}>
                                    <FormLabel sx={itemLabel}>フィールド選択</FormLabel>
                                    <FormLabel sx={explainText}>出力する項目を選択状態にします</FormLabel>
                                    <Box sx={FieldChoice}>
                                        {field.map((item) => (
                                            <FormControlLabel key={item.id} control={<Checkbox checked={fieldCheck[item.id]} onChange={toggle(item.id)} sx={{ '& .MuiSvgIcon-root': { fontSize: '18px' } }} />}
                                                label={item.label} sx={{ '& .MuiFormControlLabel-label': { fontSize: '16px' } }} />
                                        ))}
                                    </Box>
                                </FormControl>
                            </Box>
                        </ModalScroll>
                        <Box p={2} sx={{ marginTop: '-2%', marginBottom: '1%' }}>
                            <Grid container sx={{ width: '98%', justifyContent: "right" }}>
                                <CancelButton variant="contained" onClick={() => handleClose(false)}>キャンセル</CancelButton>
                                <DlButton variant="contained" onClick={() => DLCheck()}>PDFダウンロード</DlButton>
                            </Grid>
                        </Box>
                    </Box>
                </Zoom>
            </Modal >
        </>
    );
}

export default PrintDialog;
